import styles from "./cost-of-living.module.css"

import React from "react"
import cx from "classnames"
import * as util from "activities/util"
import * as fonts from "shared/fonts"
import * as shared from "activities/components/shared"
import * as inputs from "activities/components/inputs"
import assign from "object-assign"

const Title = ({children}) => <div className={cx(styles["title"], fonts.getFontClass())}>{children}</div>

const getJson = (url, {success}) => {
  const xmlhttp = new XMLHttpRequest()
  xmlhttp.open("GET", url)
  xmlhttp.setRequestHeader("Content-Type", "application/json")
  xmlhttp.send()
  xmlhttp.onreadystatechange = () => {
    if (xmlhttp.readyState === 4 && xmlhttp.status === 200) {
      success({
        status: xmlhttp.status,
        body: JSON.parse(xmlhttp.response)
      })
    }
  }
}

const stateNames = (data) => Object.keys(data).sort()

const calcPriceAdjustment = ({data, currentState, currentCounty, otherState, otherCounty}, key) => {
  const currentIndex = data[currentState][currentCounty][key]
  return (data[otherState][otherCounty][key] - currentIndex) / currentIndex
}

const getCountyNames = (data, state, selectedCounty) => Object.keys(data[state]).sort()

const updateSelectedState = (updateState, data, keys) => (selectedState) => {
  updateState(keys[0])(selectedState)
  updateState(keys[1])(getCountyNames(data, selectedState)[0])
}

const calcSalaryEquivalent = (state) => {
  const {currentSalary} = state
  return Math.round((currentSalary + currentSalary * calcPriceAdjustment(state, "composite")) / 100) * 100
}

export default shared.ActivityFactory(
  ({getProps, getState, updateState}) => {
    const state = getState()
    const {currentState, currentCounty, otherState, otherCounty, currentSalary, data, resetKey} = state
    const {id, theme} = getProps()

    if (data) {
      const stateOptions = stateNames(data)
      const _calcPriceAdjustment = (key) => calcPriceAdjustment(state, key)
      const compositeDiff = _calcPriceAdjustment("composite")
      const salaryEquivalent = calcSalaryEquivalent(state)
      return (
        <div>
          <shared.Content className={styles["content"]}>
            <shared.Calculator className={styles["calculator"]}>
              <inputs.RangeInput
                id={id}
                title="Current Salary"
                onChange={updateState("currentSalary", util.fromString)}
                initialValue={currentSalary}
                min={10000}
                max={300000}
                step={5000}
                units="$"
                color={theme["color-1"]}
                resetKey={resetKey}
              />
              <div className={styles["locations"]}>
                <div className={styles["calc-input"]}>
                  <inputs.Label>Current Location</inputs.Label>
                  <inputs.Dropdown
                    label="Select current state"
                    className={styles["dropdown"]}
                    initialValue={currentState}
                    options={stateOptions}
                    onChange={updateSelectedState(updateState, data, ["currentState", "currentCounty"])}
                    resetKey={resetKey}
                  />
                  <inputs.Dropdown
                    label="Select current city"
                    className={styles["dropdown"]}
                    initialValue={currentCounty}
                    options={getCountyNames(data, currentState)}
                    onChange={updateState("currentCounty")}
                    resetKey={resetKey}
                  />
                </div>
                <div className={styles["calc-input"]}>
                  <inputs.Label>Moving To</inputs.Label>
                  <inputs.Dropdown
                    label="Select the state you are moving to."
                    className={styles["dropdown"]}
                    initialValue={otherState}
                    options={stateOptions}
                    onChange={updateSelectedState(updateState, data, ["otherState", "otherCounty"])}
                    resetKey={resetKey}
                  />
                  <inputs.Dropdown
                    label="Select the city you are moving to."
                    className={styles["dropdown"]}
                    initialValue={otherCounty}
                    options={getCountyNames(data, otherState)}
                    onChange={updateState("otherCounty")}
                    resetKey={resetKey}
                  />
                </div>
              </div>
            </shared.Calculator>
            <div className={cx(styles["results"], fonts.getFontClass())}>
              <div className={styles["title-grid"]}>
                <Title>Comparable Salary</Title>
                <div className={cx(styles["result"], fonts.getFontClass())}>{util.formatDollarsNoCents(salaryEquivalent)}</div>
                <p>
                  {`${otherCounty} is roughly ${util.formatPercent(Math.abs(compositeDiff))} ${compositeDiff > 0 ? "more" : "less"} expensive than ${currentCounty}. `}You will need
                  to earn {util.formatDollarsNoCents(salaryEquivalent)} to maintain a similar standard of living.
                </p>
              </div>
              <div className={styles["title-grid"]}>
                <Title>Price Adjustments in {otherCounty}</Title>
                <table>
                  <tbody>
                    <shared.DifferenceRow title="Housing" difference={_calcPriceAdjustment("housing")} />
                    <shared.DifferenceRow title="Groceries" difference={_calcPriceAdjustment("groceries")} />
                    <shared.DifferenceRow title="Utilities" difference={_calcPriceAdjustment("utilities")} />
                    <shared.DifferenceRow title="Transportation" difference={_calcPriceAdjustment("transportation")} />
                    <shared.DifferenceRow title="Healthcare" difference={_calcPriceAdjustment("health-care")} />
                  </tbody>
                </table>
              </div>
            </div>
          </shared.Content>
        </div>
      )
    } else {
      getJson("https://banzai.org/calculator-data/cost-of-living", {
        success: (resp) => updateState("data")(resp.body)
      })
      return <shared.Loading />
    }
  },
  {
    stateFilter: (state) => {
      const comparableSalary = calcSalaryEquivalent(state)
      delete state.data
      return assign(state, {comparableSalary})
    },
    defaultProps: {
      id: "cost-of-living",
      title: "Cost of Living Calculator",
      subtitle: "Adjust the inputs to see what your comparable salary would be in a different city.",
      skipReset: ["data"]
    },
    defaultState: {
      currentSalary: 50000,
      currentState: "Massachusetts",
      currentCounty: "Boston",
      otherState: "California",
      otherCounty: "San Francisco",
      data: null
    }
  }
)
